import React, { useEffect, useRef, useState } from 'react';
import { useSearchResults, SearchResult, AssistantMessage } from '../../contexts/SearchResultsContext';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { useLocation } from '../../contexts/LocationContext';
import FloatingMessageBubble from './FloatingMessageBubble';
import ResultCard from './ResultCard';
import { useOasisBackend } from '../../../hooks/useOasisBackend';
import GridLayout from './GridLayout';
import { toast, ToastContainer } from 'react-toastify';

import Message from '../../components/Message';
import { useUser } from '../../../hooks/useUser';
import { useLoggedOutUserSearchCounter } from '../../hooks/useLoggedOutUserSearchCounter';

interface ResultsManagerProps {
  onFirstInteraction?: () => void;
  initialMessage?: string;
  currentAgent?: string;
  currentModel?: string;
  agentParams?: Record<string, any>;
  onSearch?: (query: string, params: { model: string; agent: string; agentParams: any }) => void;
  onMount?: (handleSendMessage: (message: string) => void, loadConversation: (id: number) => void) => void;
  conversationId?: number | null;
}

interface UIComponentMessage {
  operation: 'set' | 'append' | 'prepend' | 'insert' | 'remove';
  results?: Array<{
    type: string;
    data: any;
    loading?: boolean;
  }>;
    index?: number;
    startIndex?: number;
    count?: number; 
}

const ResultsManager: React.FC<ResultsManagerProps> = ({
  onFirstInteraction,
  initialMessage,
  currentAgent = 'search_engine',
  currentModel = 'gpt-4.1-mini',
  agentParams,
  onSearch,
  onMount,
  conversationId,
}) => {
  const { state, dispatch } = useSearchResults();
  const { ws, isWsReady } = useWebSocket();
  const { location } = useLocation();
  const db = useOasisBackend();
  const user = useUser();
  const loggedIn = user.state === "LoggedIn";
  const { incrementSearchCount, hasReachedLimit, isInitialized } = useLoggedOutUserSearchCounter(loggedIn);
  const hasTrackedFirstInteraction = useRef(false);
  const idCounter = useRef(0);
  const [conversationIdState, setConversationIdState] = useState<number | null>(conversationId || null);
  const [isLoading, setIsLoading] = useState(false);
  const [isQueryCollapsed, setIsQueryCollapsed] = useState(true);
  const [isWaitingForLocation, setIsWaitingForLocation] = useState(false);
  const pendingInitialMessage = useRef<string | null>(null);
  const currentMessageId = useRef<string | null>(null);
  const trackedResults = useRef<Set<string>>(new Set());
  const messageHandlerSetUp = useRef(false);
  const hasProcessedInitialMessage = useRef(false);
  const hasLoadedConversation = useRef(false);

  // Log WebSocket status changes
  useEffect(() => {
    console.log('🔌 WebSocket Status Update:', {
      isWsReady,
      wsExists: !!ws.current,
      wsReadyState: ws.current?.readyState,
      wsReadyStateText: ws.current ? ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][ws.current.readyState] : 'NO_WS',
      timestamp: new Date().toISOString()
    });
  }, [isWsReady, ws.current]);

  // Log WebSocket ready state changes
  useEffect(() => {
    if (ws.current) {
      const logReadyState = () => {
        console.log('🔌 WebSocket ReadyState Change:', {
          readyState: ws.current?.readyState,
          readyStateText: ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][ws.current?.readyState || 0],
          timestamp: new Date().toISOString()
        });
      };

      // Log initial state
      logReadyState();

      // Set up event listeners to track state changes
      const originalOnOpen = ws.current.onopen;
      const originalOnClose = ws.current.onclose;
      const originalOnError = ws.current.onerror;

      ws.current.onopen = (event) => {
        console.log('🔌 WebSocket Opened:', {
          event,
          timestamp: new Date().toISOString()
        });
        if (originalOnOpen) originalOnOpen(event);
      };

      ws.current.onclose = (event) => {
        console.log('🔌 WebSocket Closed:', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          timestamp: new Date().toISOString()
        });
        if (originalOnClose) originalOnClose(event);
      };

      ws.current.onerror = (event) => {
        console.log('🔌 WebSocket Error:', {
          event,
          timestamp: new Date().toISOString()
        });
        if (originalOnError) originalOnError(event);
      };
    }
  }, [ws.current]);

  const generateId = () => {
    idCounter.current += 1;
    return `id-${idCounter.current}-${Date.now()}`;
  };

  const handleMessageDismiss = (id: string) => {
    dispatch({
      type: 'REMOVE_MESSAGE',
      payload: { id }
    });
  };

  // Handle initial message when WebSocket becomes ready
  useEffect(() => {
    console.log('🔄 Initial Message Effect (WebSocket Ready):', {
      isWsReady,
      isInitialized,
      hasPendingMessage: !!pendingInitialMessage.current,
      pendingMessage: pendingInitialMessage.current,
      hasLocation: !!location,
      location: location ? `${location.city}, ${location.country_code}, ${location.postal_code} (${location.latitude}, ${location.longitude})` : 'none',
      hasProcessedInitial: hasProcessedInitialMessage.current,
      timestamp: new Date().toISOString()
    });

    if (isWsReady && pendingInitialMessage.current && location && !hasProcessedInitialMessage.current) {
      console.log('📤 Sending pending initial message with location:', pendingInitialMessage.current);
      setIsWaitingForLocation(false);
      hasProcessedInitialMessage.current = true;
      handleSendMessage(pendingInitialMessage.current);
      pendingInitialMessage.current = null;
    }
  }, [isWsReady, isInitialized, location]);

  // Handle initial message
  useEffect(() => {
    console.log('🔄 Initial Message Effect:', {
      initialMessage,
      isWsReady,
      wsExists: !!ws.current,
      wsReadyState: ws.current?.readyState,
      wsReadyStateText: ws.current ? ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][ws.current.readyState] : 'NO_WS',
      isInitialized,
      hasLocation: !!location,
      location: location ? `${location.city}, ${location.country_code}, ${location.postal_code} (${location.latitude}, ${location.longitude})` : 'none',
      hasProcessedInitial: hasProcessedInitialMessage.current,
      timestamp: new Date().toISOString()
    });
    
    if (initialMessage && isWsReady && isInitialized && location && !hasProcessedInitialMessage.current) {
      console.log('📤 Sending initial message immediately with location:', initialMessage);
      setIsWaitingForLocation(false);
      hasProcessedInitialMessage.current = true;
      handleSendMessage(initialMessage);
    } else if (initialMessage && !isInitialized && !hasProcessedInitialMessage.current) {
      console.log('⏳ Waiting for counter to initialize before sending message');
      pendingInitialMessage.current = initialMessage;
    } else if (initialMessage && !location && !hasProcessedInitialMessage.current) {
      console.log('⏳ Waiting for location before sending initial message:', initialMessage);
      pendingInitialMessage.current = initialMessage;
      setIsWaitingForLocation(true);
    } else if (initialMessage && !hasProcessedInitialMessage.current) {
      console.log('💾 Storing initial message as pending:', initialMessage);
      pendingInitialMessage.current = initialMessage;
    }
  }, [initialMessage, isWsReady, isInitialized, location]);

  const handleSendMessage = async (message: string) => {
    console.log('📤 Attempting to send message:', {
      message: message.substring(0, 100) + (message.length > 100 ? '...' : ''),
      messageLength: message.length,
      wsExists: !!ws.current,
      wsReadyState: ws.current?.readyState,
      wsReadyStateText: ws.current ? ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][ws.current.readyState] : 'NO_WS',
      isWsReady,
      loggedIn,
      hasReachedLimit,
      hasLocation: !!location,
      location: location ? `${location.city}, ${location.country_code}, ${location.postal_code} (${location.latitude}, ${location.longitude})` : 'none',
      timestamp: new Date().toISOString()
    });

    if (!message.trim() || !ws.current) {
      console.log("❌ Not sending message - empty or no WebSocket connection");
      return;
    }

    // Check if user is logged in and has reached limit
    if (!loggedIn && hasReachedLimit) {
      console.log("❌ User has reached search limit");
      return;
    }

    // Increment search count for unauthenticated users
    if (!loggedIn) {
      incrementSearchCount();
    }

    console.log('📤 Proceeding with sending message');
    setIsLoading(true);
    // Reset current message ID when starting a new message
    currentMessageId.current = null;
    
    // Reset tracked results when starting a new search
    trackedResults.current.clear();
    
    dispatch({
      type: 'SET_SEARCH_PARAMS',
      payload: {
        query: message,
        model: currentModel,
        agent: currentAgent,
        agentParams,
      },
    });

    try {
      const messageData: any = {
        message,
        model: currentModel,
        conversation_id: conversationIdState,
        agent_slug: currentAgent,
        stream: true,
        additional_arguments: {}
      };

      if (agentParams) {
        messageData.agent_params = agentParams;
      }

      // Add location data if available
      if (location) {
        messageData.additional_arguments.location = {
          latitude: location.latitude,
          longitude: location.longitude,
          country: location.country_code,
          city: location.city,
          region: location.region,
          postal_code: location.postal_code,
          source: location.source
        };
        console.log('📍 Adding IP location data to search query:', messageData.location);
      }

      console.log("📤 Sending WebSocket message:", {
        messageData,
        wsReadyState: ws.current?.readyState,
        wsReadyStateText: ws.current ? ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][ws.current.readyState] : 'NO_WS',
        timestamp: new Date().toISOString()
      });

      try {
        ws.current.send(JSON.stringify(messageData));
        console.log("✅ WebSocket message sent successfully");
      } catch (sendError) {
        console.error("❌ Error sending WebSocket message:", {
          error: sendError,
          messageData: messageData,
          wsReadyState: ws.current?.readyState,
          wsReadyStateText: ws.current ? ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][ws.current.readyState] : 'NO_WS',
          timestamp: new Date().toISOString()
        });
        throw sendError;
      }
    } catch (error) {
      console.error('❌ Error sending message:', error);
      setIsLoading(false);
    }
  };

  // Load conversation on mount if conversationId is provided
  useEffect(() => {
    if (conversationId && !hasLoadedConversation.current) {
      loadConversation(conversationId);
      hasLoadedConversation.current = true;
    }
  }, [conversationId]);

  const loadConversation = async (id: number) => {
    try {
      console.log('Loading conversation:', id);
      const response = await db.endpoints.rhea.rheaApiConversationRetrieve(id);
      
      // Reset state
      dispatch({ type: 'RESET' });
      
      // Process messages to reconstruct state
      for (const message of response.data.messages as any[]) {
        if (message.role === 'user') {
          // Create new search state for user message
          dispatch({
            type: 'SET_SEARCH_PARAMS',
            payload: {
              query: message.content,
              model: (response.data as any).model_name || currentModel,
              agent: (response.data as any).agent_slug || currentAgent,
              agentParams: (response.data as any).agent_params || agentParams,
            },
          });
        } else if (message.role === 'assistant') {
          // Add assistant message
          dispatch({
            type: 'ADD_MESSAGE',
            payload: {
              id: `reconstructed-${message.id}`,
              content: message.content,
              timestamp: new Date(message.created_at).getTime(),
            },
          });
        } else if (message.role === 'reasoning') {
          // Handle reasoning messages - add as a message with parsed duration
          try {
            const reasoningData = typeof message.content === 'string' 
              ? JSON.parse(message.content) 
              : message.content;
            dispatch({
              type: 'ADD_MESSAGE',
              payload: {
                id: `reasoning-${message.id}`,
                content: `🧠 Thought for ${reasoningData.duration || 0} seconds`,
                timestamp: new Date(message.created_at).getTime(),
                isReasoning: true,
                reasoningText: reasoningData.text,
                reasoningDuration: reasoningData.duration,
              },
            });
          } catch (error) {
            console.error('Error processing reasoning message:', error);
          }
        } else if (message.role === 'ui_component') {
          // Execute UI component message
          try {
            const uiData = JSON.parse(message.content);
            if (uiData.type === "search_engine_management") {
              const uiComponentData = uiData.data;
              if (uiComponentData.operation === 'set' && uiComponentData.results) {
                dispatch({
                  type: 'SET_RESULTS',
                  payload: uiComponentData.results.map((result: any) => ({
                    id: result.id || generateId(),
                    type: result.type,
                    data: typeof result.data === 'string' ? JSON.parse(result.data) : result.data,
                    timestamp: Date.now(),
                    loading: result.loading,
                  })),
                });
              }
            }
          } catch (error) {
            console.error('Error processing UI component message:', error);
          }
        }
      }
      
      setConversationIdState(id);
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  // Handle WebSocket messages
  useEffect(() => {
    console.log('🔌 Setting up WebSocket message handler:', {
      isWsReady,
      wsExists: !!ws.current,
      wsReadyState: ws.current?.readyState,
      wsReadyStateText: ws.current ? ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][ws.current.readyState] : 'NO_WS',
      messageHandlerSetUp: messageHandlerSetUp.current,
      timestamp: new Date().toISOString()
    });

    if (!ws.current || !isWsReady) {
      console.log('🔌 WebSocket not ready, skipping message handler setup');
      messageHandlerSetUp.current = false;
      return;
    }

    // Prevent multiple setups
    if (messageHandlerSetUp.current) {
      console.log('🔌 Message handler already set up, skipping');
      return;
    }

    console.log('🔌 Setting up message handler for the first time');
    messageHandlerSetUp.current = true;

    const handleMessage = (event: MessageEvent) => {
      console.log('📨 WebSocket message received:', {
        dataLength: event.data?.length,
        timestamp: new Date().toISOString(),
        data: event.data
      });

      try {
        const data = JSON.parse(event.data);

        if (data.type === 'error' || data.error) {
          console.error('❌ WebSocket error:', data.error);
          setIsLoading(false);
          return;
        }

        // Handle conversation ID updates within the message handler
        if (data.conversation_id && data.conversation_id !== conversationIdState) {
          console.log('🆔 New conversation ID received:', data.conversation_id);
          setConversationIdState(data.conversation_id);
          
          // Update URL with conversation ID
          window.history.replaceState({}, '', `/search/${data.conversation_id}`);
        } 

        // Debug logging for all non-content messages
        if (data.type !== 'content') {
          console.log("📨 WebSocket Message:", {
            type: data.type,
            timestamp: new Date().toISOString(),
            data: data
          });
        }

        switch (data.type) {
          case 'content':
            if (data.content) {
              if (!currentMessageId.current) {
                // Create new message if this is the first chunk
                const message: AssistantMessage = {
                  id: generateId(),
                  content: data.content,
                  timestamp: Date.now(),
                };
                currentMessageId.current = message.id;
                dispatch({ type: 'ADD_MESSAGE', payload: message });
              } else {
                // Update existing message with new content
                dispatch({
                  type: 'UPDATE_MESSAGE',
                  payload: {
                    id: currentMessageId.current,
                    content: data.content,
                  },
                });
              }
            }
            break;

          case 'reasoning':
            // Handle reasoning chunks from reasoning models - currently just log them
            // In the future, these could be displayed in a collapsible section
            console.log('🧠 Reasoning:', data.content?.substring(0, 100) + '...');
            break;

          case 'block_update':
            console.log('🔧 Block Update:', data.data?.id, data.data?.result);
            try {
              // Validate that data.data exists
              if (!data.data) {
                console.error('❌ block_update missing data.data:', data);
                break;
              }

              if (!data.data.id) {
                console.error('❌ block_update missing data.data.id:', data);
                break;
              }

              // Handle both string and already-parsed object cases
              let parsedContent;
              if (typeof data.data.result === 'string') {
                try {
                  parsedContent = JSON.parse(data.data.result);
                } catch (parseError) {
                  console.error('❌ Error parsing block_update result JSON:', {
                    error: parseError,
                    result: data.data.result,
                    blockId: data.data.id
                  });
                  // Use the string as-is if parsing fails
                  parsedContent = { error: 'Failed to parse result', raw: data.data.result };
                }
              } else if (data.data.result !== undefined && data.data.result !== null) {
                // Already an object
                parsedContent = data.data.result;
              } else {
                console.error('❌ block_update missing or null result:', data);
                break;
              }

              dispatch({
                type: 'UPDATE_BLOCK',
                payload: {
                  id: data.data.id,
                  content: parsedContent,
                  type: data.data.type || 'unknown',
                  loading: data.loading !== undefined ? data.loading : false,
                },
              });
            } catch (error) {
              console.error('❌ Error processing block_update:', {
                error,
                blockId: data.data?.id,
                data: data.data
              });
              // Don't throw - just log the error to prevent white screen
            }
            break;
          case 'ui_component':
            if (data.content) {
              try {
                const json_data = JSON.parse(data.content);
                let uiComponentData: UIComponentMessage;
                if (json_data.type === "search_engine_management") {
                  uiComponentData = json_data.data as UIComponentMessage;
                  console.log("🔧 UI Component Operation:", {
                    operation: uiComponentData.operation,
                    timestamp: new Date().toISOString(),
                    hasResults: !!uiComponentData.results,
                    resultsCount: uiComponentData.results?.length
                  });
                }
                else {
                  console.log("❓ Unknown UI component type:", json_data);
                  return;
                }
                switch (uiComponentData.operation) {
                  case 'set':
                    console.log("🔧 SET operation:", uiComponentData.results);

                    if (!uiComponentData.results) {
                      throw new Error('Results required for set operation');
                    }
                    dispatch({
                      type: 'SET_RESULTS',
                      payload: uiComponentData.results.map(result => ({
                        id: (result as any).id || generateId(), // Use backend ID if available, fallback to generated
                        type: result.type,
                        data: result.data,
                        timestamp: Date.now(),
                        loading: result.loading,
                      })),
                    });
                    break;
                }
                } catch (err) {
                  console.error('❌ Error processing UI component data:', err);
                }
            }
            break;
          
          case 'tool_call':
            console.log('🔧 Tool Call:', data.tool_call);
            // Check if this is a search tool call
            if (data.tool_call?.function?.name === 'search') {
              const searchQuery = data.tool_call.function.arguments?.query;
              if (searchQuery) {
                toast.info(`🔍 Searching for: "${searchQuery}"`, {
                  position: "top-right",
                  autoClose: 3000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true,
                });
              }
            }
            break;

          case 'done':
            console.log('✅ WebSocket stream completed');
            setIsLoading(false);
            currentMessageId.current = null;
            break;
        }
      } catch (err) {
        console.error('❌ Error processing WebSocket message:', err);
        setIsLoading(false);
      }
    };

    ws.current.onmessage = handleMessage;

    return () => {
      if (ws.current) {
        console.log('🔌 Cleaning up WebSocket message handler');
        ws.current.onmessage = null;
        messageHandlerSetUp.current = false;
      }
    };
  }, [ws, dispatch, isWsReady]);

  // Track first interaction
  useEffect(() => {
    if (!hasTrackedFirstInteraction.current && onFirstInteraction && state.currentState.results.length > 0) {
      hasTrackedFirstInteraction.current = true;
      onFirstInteraction();
    }
  }, [state.currentState.results, onFirstInteraction]);


  // Helper function to track user interactions
  const trackInteraction = async (type: 'action' | 'web' | 'google_it' | 'chat' | 'image' | 'video' | 'infobox' | 'table' | 'code' | 'markdown' | 'link' | 'unknown', actionId?: number) => {
    const query = state.currentState.searchParams.query;
    if (!query) return;
    
    // Use setTimeout to ensure this runs asynchronously and doesn't block the redirect
    setTimeout(async () => {
      try {
        await db.endpoints.rhea.rheaApiAddQueryResultMatchCreate({
          query: query,
          type: type,
          action: actionId || 0, // Use 0 as default for non-action types
        } as any); // Use 'as any' to bypass the type check since we're only sending creation fields
      } catch (error) {
        console.error('Failed to track interaction:', error);
        // Silently fail - tracking shouldn't affect user experience
      }
    }, 0);
  };

  // Tracking callback for result cards - ensures each result is only tracked once
  const handleTrack = (resultId: string, type: string) => {
    // Only track if we haven't tracked this result before
    if (!trackedResults.current.has(resultId)) {
      trackedResults.current.add(resultId);
      trackInteraction(type as any);
    }
  };

  const handleGoogleIt = () => {
    if (state.currentState.searchParams.query) {
      trackInteraction('google_it');
      window.open(`https://google.com/search?q=${encodeURIComponent(state.currentState.searchParams.query)}`, '_blank');
    }
  };

  useEffect(() => {
    if (onMount) {
      onMount(handleSendMessage, loadConversation);
    }
  }, [onMount, handleSendMessage, loadConversation]);

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-7xl mx-auto">
        {/* Query Display */}
        {state.currentState.searchParams.query && (
          <div className="mb-6 flex items-center gap-3">
            <div className="flex-1 cursor-pointer" onClick={() => setIsQueryCollapsed(!isQueryCollapsed)}>
              <Message
                content={`${state.currentState.searchParams.query}`}
                role="user"
                isCollapsed={isQueryCollapsed}
              />
            </div>
            {/* Just Google It Button */}
            <button
              onClick={handleGoogleIt}
              className="flex items-center justify-center hover:gap-2 px-2 py-2 bg-[var(--rhea-card-bg)] border border-[var(--rhea-card-border)] rounded-lg hover:shadow-lg transition-all duration-300 hover:scale-[1.02] shadow-md shrink-0 group overflow-hidden min-w-[40px] hover:min-w-[140px]"
              style={{ 
                backgroundColor: 'var(--rhea-card-bg)',
                border: '1px solid var(--rhea-card-border)'
              }}
            >
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/c/c1/Google_%22G%22_logo.svg/800px-Google_%22G%22_logo.svg.png" 
                alt="Google" 
                className="h-4"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
              <div className="hidden h-4 w-4 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold">G</div>
              <span className="text-[var(--rhea-text-primary)] font-medium text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 max-w-0 group-hover:max-w-[100px]"> Just Google It</span>
            </button>
          </div>
        )}
        
        {/* Results Grid */}
        {(() => {
          // Filter results based on focus state
          const visibleResults = state.currentState.results;

          return (
            <div className="min-h-[calc(100vh-200px)]">
              <GridLayout 
                items={visibleResults} 
                onTrack={handleTrack}
              />
            </div>
          );
        })()}

        {/* Loading Indicator */}
        {isLoading && (
          <div className="fixed bottom-24 right-6">
            <div className="bg-[var(--rhea-bg-secondary)] border border-[var(--rhea-card-border)] rounded-lg p-3 shadow-lg">
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-[var(--rhea-accent-primary)] border-t-transparent"></div>
                <span className="text-sm text-[var(--rhea-text-primary)]">Searching...</span>
              </div>
            </div>
          </div>
        )}

        {/* Location Waiting Indicator */}
        {isWaitingForLocation && (
          <div className="fixed bottom-24 right-6">
            <div className="bg-[var(--rhea-bg-secondary)] border border-[var(--rhea-card-border)] rounded-lg p-3 shadow-lg">
              <div className="flex items-center space-x-2">
                <div className="animate-pulse rounded-full h-4 w-4 bg-blue-500"></div>
                <span className="text-sm text-[var(--rhea-text-primary)]">Getting your location...</span>
              </div>
            </div>
          </div>
        )}

        {/* Floating Messages */}
        <div className="fixed bottom-[120px] right-6 space-y-2">
          {state.currentState.messages
            .filter(message => message.content)
            .map((message) => (
              <FloatingMessageBubble
                key={message.id}
                message={message}
                onDismiss={handleMessageDismiss}
              />
            ))}
        </div>
        
        {/* Toast Container */}
        <ToastContainer />
      </div>
    </div>
  );
};

export default ResultsManager; 